import React, { useState, useEffect } from 'react';
import { X, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';

const EMOJIS = ['🦄', '🦖', '🚀', '⚽', '🎨', '🐼', '🦋', '🤖', '👑', '🌈', '🐯', '🎸'];
const COLORS = ['#FF6B35', '#7C3AED', '#0EA5E9', '#10B981', '#F59E0B', '#EC4899', '#EF4444', '#6366F1'];

export const KidProfileDialog = ({ api, kid, onClose, onSaved }) => {
  const [form, setForm] = useState({
    name: kid?.name || '', age: kid?.age || '', grade: kid?.grade || '', school: kid?.school || '',
    avatar_emoji: kid?.avatar_emoji || '🦄', avatar_color: kid?.avatar_color || '#FF6B35', pin: '',
    member_id: kid?.member_id || '', interests: (kid?.interests || []).join(', '),
    handicap: kid?.handicap || 1,
  });
  const [saving, setSaving] = useState(false);
  const [members, setMembers] = useState([]);

  useEffect(() => {
    api.get('/members').then(r => setMembers(r.data)).catch(() => {});
  }, [api]);

  const pickMember = (memberId) => {
    const m = members.find(x => x.member_id === memberId);
    if (m) {
      const age = m.dob ? Math.floor((Date.now() - new Date(m.dob)) / 31557600000) : form.age;
      setForm({ ...form, member_id: memberId, name: m.name, age: age || form.age });
    } else {
      setForm({ ...form, member_id: '' });
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, age: form.age ? parseInt(form.age) : null,
        interests: form.interests.split(',').map(s => s.trim()).filter(Boolean) };
      if (!payload.pin) delete payload.pin;
      if (kid) await api.put(`/kids/${kid.kid_id}`, payload);
      else await api.post('/kids', payload);
      toast.success(kid ? 'Profile updated' : `${form.name} added!`);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete ${kid.name}'s profile and all their data?`)) return;
    try { await api.delete(`/kids/${kid.kid_id}`); toast.success('Deleted'); onSaved(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()} data-testid="kid-profile-dialog">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-xl font-bold">{kid ? `Edit ${kid.name}` : 'Add Kid'}</h2>
          <button onClick={onClose} className="p-1 text-muted-foreground hover:text-foreground"><X size={18} /></button>
        </div>
        <form onSubmit={handleSave} className="space-y-4">
          {!kid && (
            <div>
              <label className="text-xs font-medium text-muted-foreground">Link to family member</label>
              <select data-testid="kid-member-select" value={form.member_id} onChange={e => pickMember(e.target.value)}
                className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-sm">
                <option value="">— New person (auto-adds to Members) —</option>
                {members.map(m => <option key={m.member_id} value={m.member_id}>{m.name}{m.relation ? ` (${m.relation})` : ''}</option>)}
              </select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="text-xs font-medium text-muted-foreground">Name *</label>
              <input data-testid="kid-name-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required
                className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-sm" placeholder="e.g. Aarav" />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Age</label>
              <input data-testid="kid-age-input" type="number" min="1" max="18" value={form.age} onChange={e => setForm({ ...form, age: e.target.value })}
                className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-sm" />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Class / Grade</label>
              <input data-testid="kid-grade-input" value={form.grade} onChange={e => setForm({ ...form, grade: e.target.value })}
                className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-sm" placeholder="e.g. 4th" />
            </div>
            <div className="col-span-2">
              <label className="text-xs font-medium text-muted-foreground">School</label>
              <input value={form.school} onChange={e => setForm({ ...form, school: e.target.value })}
                className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-sm" />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Interests <span className="font-normal">(comma separated — used by AI for weekend plans & chores)</span></label>
            <input data-testid="kid-interests-input" value={form.interests} onChange={e => setForm({ ...form, interests: e.target.value })}
              placeholder="e.g. animals, science experiments, cycling" className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-sm" />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Avatar</label>
            <div className="flex gap-1.5 flex-wrap mt-1">
              {EMOJIS.map(e => (
                <button type="button" key={e} onClick={() => setForm({ ...form, avatar_emoji: e })}
                  className={`w-9 h-9 rounded-lg text-lg flex items-center justify-center border ${form.avatar_emoji === e ? 'border-[#FF6B35] bg-[#FF6B35]/10' : 'border-border'}`}>{e}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Color</label>
            <div className="flex gap-1.5 flex-wrap mt-1">
              {COLORS.map(c => (
                <button type="button" key={c} onClick={() => setForm({ ...form, avatar_color: c })}
                  className={`w-8 h-8 rounded-full border-2 ${form.avatar_color === c ? 'border-foreground scale-110' : 'border-transparent'}`}
                  style={{ backgroundColor: c }} />
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Leaderboard boost <span className="font-normal">(fairness for younger kids — multiplies weekly score)</span></label>
            <select data-testid="kid-handicap-select" value={form.handicap} onChange={e => setForm({ ...form, handicap: parseFloat(e.target.value) })}
              className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-sm">
              <option value={1}>×1 (none)</option><option value={1.25}>×1.25</option>
              <option value={1.5}>×1.5</option><option value={2}>×2</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">{kid ? 'New PIN (leave blank to keep current)' : 'Kid Mode PIN (4 digits)'}</label>
            <input data-testid="kid-pin-input" value={form.pin} onChange={e => setForm({ ...form, pin: e.target.value.replace(/\D/g, '').slice(0, 4) })}
              inputMode="numeric" maxLength={4} placeholder="••••"
              className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-sm tracking-[0.5em] font-mono" />
            <p className="text-[11px] text-muted-foreground mt-1">Used by the kid to log into Kid Mode on a tablet or shared device.</p>
          </div>
          <div className="flex gap-2 pt-2">
            <button type="submit" disabled={saving} data-testid="kid-save-btn"
              className="flex-1 px-4 py-2.5 rounded-full bg-[#FF6B35] text-white btn-glow text-sm font-semibold disabled:opacity-50">
              {saving ? 'Saving…' : (kid ? 'Save Changes' : 'Add Kid')}
            </button>
            {kid && (
              <button type="button" onClick={handleDelete} data-testid="kid-delete-btn"
                className="px-4 py-2.5 rounded-full border border-destructive/40 text-destructive text-sm font-semibold flex items-center gap-1.5">
                <Trash size={14} /> Delete
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};
